import React from 'react'

import { connect } from 'react-redux'

import { dashboardActions } from '@services'

import { bindActionToPromise, cancellablePromise, isAdmin, parseApiErrorMessage } from '@helpers'

import { useAssertContext, useCancellablePromiseRef } from '@hooks'

import { CompanyConsentDialog } from './CompanyConsentDialog'
import type { CompanyConsentsResponse, CompanyConsentTypes } from './types'

type CallWithConsentCheckFn = (consent: CompanyConsentTypes, callback: VoidFunction) => VoidFunction

const CompanyConsentContext = React.createContext<Nullable<CallWithConsentCheckFn>>(null)

CompanyConsentContext.displayName = 'CompanyConsentContext'

type ConsentProviderState = {
  error: BackendError
  forced: boolean
  loading: boolean
  open: boolean
  originalRequestFunction: Nullable<VoidFunction>
  consentType: Nullable<CompanyConsentTypes>
}

interface CompanyConsentProviderProps {
  children: React.ReactNode
  getConsents: AsyncFunction<void, CompanyConsentsResponse>
  isAdminUser: boolean
  setConsent: AsyncFunction<CompanyConsentTypes, CompanyConsentsResponse>
}

function PureCompanyConsentProvider({ children, getConsents, isAdminUser, setConsent }: CompanyConsentProviderProps) {
  const [consentState, setConsentState] = React.useState<Nullable<CompanyConsentsResponse>>(null)
  const [{ error, forced, loading, open, originalRequestFunction, consentType }, setState] =
    React.useState<ConsentProviderState>({
      error: null,
      forced: false,
      loading: false,
      open: false,
      originalRequestFunction: null,
      consentType: null,
    })
  const cPromiseRef = useCancellablePromiseRef<CompanyConsentsResponse>()

  const consents = consentState?.consents

  React.useEffect(() => {
    async function fetchConsents() {
      try {
        cPromiseRef.current = cancellablePromise(getConsents())
        const state = await cPromiseRef.current.promise
        setConsentState(state)

        const forcedConsent = state.pending.find(({ force }) => force)
        if (forcedConsent) {
          setState(prevState => ({ ...prevState, forced: true, open: true, consentType: forcedConsent.type }))
        }
      } catch (_error) {
        // silently ignore error
      }
    }
    fetchConsents()
  }, [cPromiseRef, getConsents])

  const handleConfirm = React.useCallback(async () => {
    if (!consentType) {
      return
    }
    try {
      setState(prevState => ({ ...prevState, error: null, loading: true }))
      const results = await setConsent(consentType)
      setConsentState(results)
      // call callback
      originalRequestFunction?.()
      // close dialog
      setState(prevState => ({ ...prevState, open: false, loading: false }))
    } catch (error) {
      const errorMessage = parseApiErrorMessage(error)
      if (errorMessage) {
        setState(prevState => ({
          ...prevState,
          error: errorMessage,
          loading: false,
        }))
      }
    }
  }, [consentType, originalRequestFunction, setConsent])

  const callWithConsentCheck = React.useCallback<CallWithConsentCheckFn>(
    (requiredConsent, callbackFn) => () => {
      if (consents?.[requiredConsent]) {
        setState(prevState => ({
          ...prevState,
          forced: false,
          originalRequestFunction: callbackFn,
          consentType: requiredConsent,
        }))
        callbackFn()
      } else {
        setState(prevState => ({
          ...prevState,
          forced: false,
          open: true,
          originalRequestFunction: callbackFn,
          consentType: requiredConsent,
        }))
      }
    },
    [consents]
  )

  return (
    <CompanyConsentContext.Provider value={callWithConsentCheck}>
      {children}
      <CompanyConsentDialog
        consentType={consentType}
        error={error}
        forced={forced}
        isAdminUser={isAdminUser}
        loading={loading}
        onClose={() => setState(prevState => ({ ...prevState, open: false }))}
        onConfirm={handleConfirm}
        open={open}
      />
    </CompanyConsentContext.Provider>
  )
}

export const CompanyConsentProvider = connect(
  (state: Store) => ({
    isAdminUser: isAdmin(state.auth.company.data.role),
  }),
  dispatch => ({
    getConsents: bindActionToPromise(dispatch, dashboardActions.getCompanyConsents.request),
    setConsent: bindActionToPromise(dispatch, dashboardActions.setCompanyConsent.request),
  })
)(PureCompanyConsentProvider)

CompanyConsentProvider.displayName = 'CompanyConsentProvider'

export function useCompanyConsent() {
  return useAssertContext(CompanyConsentContext)
}
